import { Link, useLocation } from "react-router-dom";

import { Case, LISCase } from "pwapi";

import { ExternalLabList, LabList } from "custom-models/frontLoader";

import { useIsLoadingAny } from "shared/hooks/requestStatusHooks";
import { useCanSeeFrontEndAdmin } from "shared/hooks/usePermissions";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { displayPriority, getBillingProperties } from "shared/utils/caseUtils";
import {
  getBillingTypeOptions,
  isNoBillingConstrainedCaseType,
} from "shared/utils/rulesEngineUtils";

import { setSpecimenBag } from "app/actions/specimenBagActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { updateCase } from "app/reducers/admin/actions/caseActions";
import { CaseState } from "app/reducers/caseReducer";
import AccessionNumber from "ui/components/AccessionNumber";

const CaseDetailHeader = ({
  case_id,
  specimenCase,
  lab,
  externalLabs,
  externalAccession,
  specimenBagBarcode,
  setExternalAccession,
  requireExternalAccession,
  userMadeBillingTypeSelection,
  initialBillingType,
  onUpdateCase,
  onAccessionCase,
}: {
  case_id: number;
  specimenCase: CaseState;
  lab: LabList;
  externalAccession?: string | null;
  setExternalAccession: Function;
  specimenBagBarcode?: string | null;
  requireExternalAccession?: boolean;
  userMadeBillingTypeSelection?: boolean;
  initialBillingType: "" | LISCase.billing_type;
  externalLabs: ExternalLabList[];
  onUpdateCase: Function;
  onAccessionCase: Function;
}) => {
  const dispatch = useAppDispatch();
  const { state: navigationState } = useLocation();

  const loadingUpdate = useIsLoadingAny(updateCase);

  const canSeeAdmin = useCanSeeFrontEndAdmin();
  const lisHistoryLink = useAppSelector((state) =>
    selectFeatureFlag(state, "lisHistoryLink")
  );
  const lisBarcodesLink = useAppSelector((state) =>
    selectFeatureFlag(state, "lisBarcodesLink")
  );
  const externalGrossingLab = useAppSelector((state) =>
    selectFeatureFlag(state, "externalGrossingLab")
  );

  const patient = specimenCase?.patient;

  const clinic = useAppSelector((state) =>
    patient?.clinic_id ? state.clinics[patient.clinic_id] : undefined
  );

  const disableAccessionButtonDueToBillingType = (() => {
    // A bit nicer to have this functionality in an inline function,
    // ... because it was otherwise a bit difficult to read inline
    const forceUserToMakeSelection = userMadeBillingTypeSelection
      ? !specimenCase?.billing_type
      : !initialBillingType;

    // If the case type is one of the constrained (no-billing) types, don't disable
    // accessioning due to billing type, because the user can't choose a billing type anyway
    if (isNoBillingConstrainedCaseType(specimenCase?.type)) return false;

    // If the case is currently PATCHing on either Priority or Billing Type, disable
    if (loadingUpdate) return true;

    // If the case has no billing type, disable
    if (!specimenCase?.billing_type) return true;

    // If the user is forced to make a selection, disable
    if (forceUserToMakeSelection) return true;

    // If the case has no billing types (is misconfigured),
    // ... it will show all options. So, don't disable.
    if (!clinic?.billing_types?.length) return false;

    // Otherwise, if the case's billing type is set to something other
    // ... that what is available, disable it
    if (!clinic?.billing_types?.includes(specimenCase?.billing_type))
      return true;

    // When in doubt, don't disable
    return false;
  })();

  const disableAccessionButtonDueToInsurancePlan = (() => {
    // If the lab doesn't even use the plan management, don't disable the button
    if (lab.use_manual_insurance) return false;

    // If any insurance comes back which exists, is not manual, and missing an insurance plan, disable the button
    if (
      specimenCase?.patient?.insurance &&
      specimenCase?.patient?.insurance?.find(
        (insur) => insur && !insur.is_manual && !insur.plan
      )
    )
      return true;
  })();

  const allReceived = specimenCase?.specimens?.every(
    (s) => s.lis_state === "received"
  );

  return (
    <header className="Case__header">
      <figure className="Logo">
        <Link to={`/cases${navigationState?.search || ""}`}>
          <img src="/logo.svg" alt="Back to case list" />
        </Link>
      </figure>
      {specimenCase?.accession && (
        <AccessionNumber accession={specimenCase?.accession} />
      )}
      <select
        value={specimenCase?.priority || "normal"}
        onChange={(e) => onUpdateCase({ priority: e.target.value })}
      >
        <option value="" disabled>
          Choose priority...
        </option>
        {Object.keys(Case.priority).map((key) => (
          <option key={key} value={key.toLowerCase()}>
            {displayPriority(key)}
          </option>
        ))}
      </select>
      {specimenCase?.patient?.last_name}, {specimenCase?.patient?.first_name}
      <span>
        ({specimenCase?.patient?.sex} &ndash;{" "}
        {specimenCase?.patient?.date_of_birth})
      </span>
      <div>
        {specimenCase?.status === Case.status.ORDERED &&
          lab?.settings?.bag_tracking?.active && (
            <label className="Case__header__input">
              <span>Specimen Bag</span>
              <input
                name="lis_accession_source__specimen_bag_barcode"
                type="text"
                value={specimenBagBarcode || ""}
                onChange={(e) =>
                  dispatch(
                    setSpecimenBag({ specimen_bag_payload: e.target.value })
                  )
                }
              />
            </label>
          )}
        {specimenCase?.status === Case.status.ORDERED &&
          externalGrossingLab &&
          !!externalLabs?.length && (
            <label className="Case__header__select">
              <span>Gross Externally At</span>
              <select
                onChange={(e) =>
                  onUpdateCase({ grossed_by_external_lab_id: e.target.value })
                }
                value={specimenCase?.grossed_by_external_lab_id}
              >
                <option key="none" value=""></option>
                {externalLabs.map((externalLab) => (
                  <option key={externalLab.id} value={externalLab.id}>
                    {externalLab.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        {specimenCase?.status === Case.status.ORDERED &&
          requireExternalAccession && (
            <label className="Case__header__input">
              <span>External Accession</span>
              <input
                type="text"
                required
                value={
                  externalAccession || specimenCase?.external_accession || ""
                }
                onChange={(e) => setExternalAccession(e.target.value)}
              />
            </label>
          )}
        {specimenCase?.status === Case.status.ORDERED && (
          <>
            {lab.settings?.set_eorder_case_type?.active && (
              <label className="Case__header__select">
                <span>Case Type</span>
                <select
                  onChange={(e) => {
                    if (!e.target.value) return;

                    const nextType = e.target.value;
                    const wasConstrained = isNoBillingConstrainedCaseType(
                      specimenCase?.type
                    );
                    const nextIsConstrained =
                      isNoBillingConstrainedCaseType(nextType);

                    if (nextIsConstrained) {
                      onUpdateCase({
                        type: nextType,
                        billing_type: LISCase.billing_type.NONE,
                        ...getBillingProperties(null),
                      });
                      return;
                    }

                    if (wasConstrained) {
                      // Switching from a constrained (no-billing) type to an
                      // unconstrained one — clear billing_type so the user
                      // must choose a value valid for the clinic.
                      onUpdateCase({
                        type: nextType,
                        billing_type: null,
                        ...getBillingProperties(null),
                      });
                      return;
                    }

                    onUpdateCase({
                      type: nextType,
                    });
                  }}
                  value={specimenCase.type}
                >
                  {[
                    { label: "", value: Case.type.CLINICAL },
                    {
                      label: "TC-PC Split",
                      value: Case.type.TC_PC,
                    },
                    {
                      label: "Triage",
                      value: Case.type.TRIAGE,
                    },
                    { label: "Test", value: Case.type.TEST },
                  ].map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="Case__header__select">
              <span>Billing Type</span>
              <select
                onChange={(e) =>
                  e.target.value &&
                  onUpdateCase({ billing_type: e.target.value })
                }
                disabled={isNoBillingConstrainedCaseType(specimenCase?.type)}
                value={
                  userMadeBillingTypeSelection
                    ? specimenCase?.billing_type || ""
                    : initialBillingType
                }
              >
                <option value="" disabled>
                  Select billing type...
                </option>
                {getBillingTypeOptions(
                  clinic?.billing_types || [],
                  specimenCase?.type
                ).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="Case__header__select">
              <span>Self Pay / Indigent</span>
              <select
                onChange={(e) =>
                  onUpdateCase({ ...getBillingProperties(e.target.value) })
                }
                disabled={isNoBillingConstrainedCaseType(specimenCase?.type)}
                value={
                  specimenCase.indigent
                    ? "indigent"
                    : specimenCase.self_pay_with_attestation
                    ? "self_pay_with_attestation"
                    : specimenCase.self_pay
                    ? "self_pay"
                    : ""
                }
              >
                <option value="">Not Applicable</option>
                {[
                  {
                    label: "Self Pay",
                    value: "self_pay",
                  },
                  {
                    label: "Self Pay With Attestation",
                    value: "self_pay_with_attestation",
                  },
                  {
                    label: "Indigent",
                    value: "indigent",
                  },
                ].map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        {specimenCase?.status === Case.status.ORDERED && (
          <button
            disabled={
              !allReceived ||
              disableAccessionButtonDueToBillingType ||
              disableAccessionButtonDueToInsurancePlan ||
              !specimenCase.collected_date ||
              (requireExternalAccession ? !externalAccession : false)
            }
            title={[
              ...(!allReceived ? ["Specimens have not been received"] : []),
              ...(disableAccessionButtonDueToBillingType
                ? ["Please choose a billing type"]
                : []),
              ...(disableAccessionButtonDueToInsurancePlan
                ? ["Insurance plan details have not been confirmed"]
                : []),
            ].join("\n")}
            className="Case__header__accession Button Button--green"
            onClick={() => onAccessionCase()}
          >
            Accession Case
          </button>
        )}
        {lisBarcodesLink && (
          <Link to="barcodes" className="Case__header__edit">
            Barcodes
          </Link>
        )}
        {lisHistoryLink && (
          <Link to="history" className="Case__header__edit">
            History
          </Link>
        )}
        {canSeeAdmin && (
          <Link
            to={`${process.env.REACT_APP_BACKEND_PROTOCOL}://${process.env.REACT_APP_VIEWER_DOMAIN}/admin/cases/${case_id}`}
            className="Case__header__edit"
            target="_blank"
          >
            Edit Case in dot. Admin ↗
          </Link>
        )}
      </div>
    </header>
  );
};

export default CaseDetailHeader;
